import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  MessageSquare,
  Send,
  History,
  Users,
  Filter,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Phone,
  MapPin,
  Info,
  Search,
  FileText,
  Building2,
  UserCheck,
} from "lucide-react";

export default function MessagingCentre({ user }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const department = user?.department || "";
  const isRevenue = department === "revenue";

  const [activeView, setActiveView] = useState("compose");

  // Step 1: Recipient type
  const [recipientType, setRecipientType] = useState("");

  // Applicant flow
  const [hoblis, setHoblis] = useState([]);
  const [circles, setCircles] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selHobli, setSelHobli] = useState("");
  const [selCircle, setSelCircle] = useState("");
  const [selVillage, setSelVillage] = useState("");
  const [farmers, setFarmers] = useState([]);
  const [selectedFarmers, setSelectedFarmers] = useState(new Set());
  const [previewing, setPreviewing] = useState(false);
  const [showFarmers, setShowFarmers] = useState(false);

  // Official flow
  const [officeOfficials, setOfficeOfficials] = useState([]);
  const [fieldOfficials, setFieldOfficials] = useState([]);
  const [selectedOfficials, setSelectedOfficials] = useState(new Set());
  const [loadingOfficials, setLoadingOfficials] = useState(false);

  // Message
  const [templates, setTemplates] = useState([]);
  const [resetKey, setResetKey] = useState(0);
  const [sendSuccess, setSendSuccess] = useState("");

  // History
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedMsg, setExpandedMsg] = useState(null);

  // Load templates on mount
  useEffect(() => {
    api.get("/messaging/templates").then(r => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  // Load cascading filters when recipient type is applicant
  const [loadingFilters, setLoadingFilters] = useState(false);
  useEffect(() => {
    if (recipientType !== "applicant") return;
    loadCascadingFilters();
  }, [recipientType, selHobli, selCircle]);

  const loadCascadingFilters = async () => {
    setLoadingFilters(true);
    try {
      const params = new URLSearchParams();
      if (selHobli) params.set("hobli", selHobli);
      if (selCircle) params.set("circle", selCircle);
      const res = await api.get(`/messaging/cascading-filters?${params}`);
      setHoblis(res.data.hoblis || []);
      setCircles(res.data.circles || []);
      setVillages(res.data.villages || []);
    } catch (err) {
      console.error("Failed to load filters:", err);
    } finally {
      setLoadingFilters(false);
    }
  };

  // Load officials when recipient type is official
  useEffect(() => {
    if (recipientType !== "official") return;
    loadOfficials();
  }, [recipientType]);

  const loadOfficials = async () => {
    setLoadingOfficials(true);
    try {
      const res = await api.get("/messaging/officials-list");
      setOfficeOfficials(res.data.office_level || []);
      setFieldOfficials(res.data.field_level || []);
    } catch (err) {
      console.error("Failed to load officials:", err);
    } finally {
      setLoadingOfficials(false);
    }
  };

  // Reset on recipient type change
  const handleRecipientTypeChange = (type) => {
    setRecipientType(type);
    setSelHobli("");
    setSelCircle("");
    setSelVillage("");
    setFarmers([]);
    setSelectedFarmers(new Set());
    setShowFarmers(false);
    setSelectedOfficials(new Set());
    setResetKey(k => k + 1);
  };

  // Cascading: reset circle/village when hobli changes
  const handleHobliChange = (val) => {
    setSelHobli(val === "all" ? "" : val);
    setSelCircle("");
    setSelVillage("");
    setShowFarmers(false);
  };

  const handleCircleChange = (val) => {
    setSelCircle(val === "all" ? "" : val);
    setSelVillage("");
    setShowFarmers(false);
  };

  const handleVillageChange = (val) => {
    setSelVillage(val === "all" ? "" : val);
    setShowFarmers(false);
  };

  // Preview applicants
  const handlePreviewApplicants = async () => {
    setPreviewing(true);
    try {
      const res = await api.post("/messaging/preview-applicants", {
        hobli: selHobli || null,
        circle: selCircle || null,
        village: selVillage || null,
      });
      const list = res.data.farmers || [];
      setFarmers(list);
      setSelectedFarmers(new Set(list.map(f => f.farmer_id).filter(Boolean)));
      setShowFarmers(true);
    } catch (err) {
      toast.error(t("messaging.sendError"));
    } finally {
      setPreviewing(false);
    }
  };

  // Toggle farmer/official selection
  const toggleFarmer = (id) => {
    setSelectedFarmers(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleOfficial = (id) => {
    setSelectedOfficials(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllOfficials = (list) => {
    setSelectedOfficials(prev => {
      const next = new Set(prev);
      list.forEach(o => next.add(o.id));
      return next;
    });
  };

  const deselectAllOfficials = (list) => {
    setSelectedOfficials(prev => {
      const next = new Set(prev);
      list.forEach(o => next.delete(o.id));
      return next;
    });
  };

  // Send callback for MessageCompose child
  const handleSendMessage = async (msgText, templateId) => {
    const ids = recipientType === "applicant"
      ? Array.from(selectedFarmers).filter(Boolean)
      : Array.from(selectedOfficials).filter(Boolean);

    if (ids.length === 0) { toast.error(t("messaging.noRecipients")); return; }

    const filtersUsed = recipientType === "applicant"
      ? { hobli: selHobli, circle: selCircle, village: selVillage }
      : { recipient_type: "official" };

    const res = await api.post("/messaging/send", {
      recipient_type: recipientType,
      recipient_ids: ids,
      message: msgText.trim(),
      template_id: templateId || null,
      filters_used: filtersUsed,
    });
    // Keep recipientType, reset the rest
    setSelHobli("");
    setSelCircle("");
    setSelVillage("");
    setFarmers([]);
    setSelectedFarmers(new Set());
    setShowFarmers(false);
    setSelectedOfficials(new Set());
    setResetKey(k => k + 1);
    setSendSuccess(`${t("messaging.sendSuccess")} (${res.data.recipients_count} ${t("messaging.recipients")})`);
    return res.data.recipients_count;
  };

  // History
  const loadHistory = useCallback(async (page = 1) => {
    setLoadingHistory(true);
    try {
      const res = await api.get(`/messaging/history?page=${page}&page_size=10`);
      setHistory(res.data.messages || []);
      setHistoryTotal(res.data.total || 0);
      setHistoryPage(page);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (activeView === "history") loadHistory(1);
  }, [activeView, loadHistory]);

  // Designation label helper
  const desigLabel = (d) => t(`designation.${d}`) !== `designation.${d}` ? t(`designation.${d}`) : d?.replace(/_/g, " ");

  // Memoize dropdown options to avoid re-render on every keystroke/state change
  const filteredHoblis = useMemo(() => hoblis.filter(h => h.toLowerCase() !== "kuduru"), [hoblis]);
  const memoizedCircles = useMemo(() => circles, [circles]);
  const memoizedVillages = useMemo(() => villages, [villages]);
  const memoizedFarmers = useMemo(() => farmers, [farmers]);

  const recipientTypeLabel = recipientType === "applicant"
    ? (lang === "kn" ? "ಅರ್ಜಿದಾರರು" : "Applicants")
    : recipientType === "official"
      ? (isRevenue ? (lang === "kn" ? "ಕಂದಾಯ ಅಧಿಕಾರಿ" : "Revenue Official") : (lang === "kn" ? "ಸರ್ವೆ ಅಧಿಕಾರಿ" : "Survey Official"))
      : "";

  return (
    <div className="space-y-4 pb-20 md:pb-4" data-testid="messaging-centre">
      {/* Mocked Banner */}
      <div className="flex items-start gap-2 p-3 bg-amber-50 border-2 border-amber-300 rounded-lg" data-testid="mms-mocked-banner">
        <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-700">{t("messaging.mockedBanner")}</p>
      </div>

      {/* View Switcher */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={activeView === "compose" ? "default" : "outline"}
          className={activeView === "compose" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          onClick={() => setActiveView("compose")}
          data-testid="messaging-compose-tab"
        >
          <MessageSquare className="w-4 h-4 mr-1.5" />
          {t("messaging.compose")}
        </Button>
        <Button
          size="sm"
          variant={activeView === "history" ? "default" : "outline"}
          className={activeView === "history" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          onClick={() => setActiveView("history")}
          data-testid="messaging-history-tab"
        >
          <History className="w-4 h-4 mr-1.5" />
          {t("messaging.history")}
        </Button>
      </div>

      {/* ===== COMPOSE VIEW ===== */}
      {activeView === "compose" && (
        <div className="space-y-4">
          {/* Step 1: Recipient Type - compact inline */}
          <div className="flex items-center gap-3 flex-wrap" data-testid="filter-recipient-row">
            <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-emerald-600" />
              {t("messaging.filterRecipient") || "Filter Recipient"}:
            </span>
            <Select value={recipientType} onValueChange={handleRecipientTypeChange}>
              <SelectTrigger className="h-8 text-xs w-[200px] sm:w-[220px]" data-testid="recipient-type-select">
                <SelectValue placeholder={t("messaging.selectRecipientType") || "Select Recipient Type"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="applicant">
                  <span className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" />
                    {lang === "kn" ? "ಅರ್ಜಿದಾರರು" : "Applicants"}
                  </span>
                </SelectItem>
                <SelectItem value="official">
                  <span className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5" />
                    {isRevenue
                      ? (lang === "kn" ? "ಕಂದಾಯ ಅಧಿಕಾರಿ" : "Revenue Official")
                      : (lang === "kn" ? "ಸರ್ವೆ ಅಧಿಕಾರಿ" : "Survey Official")}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            {recipientType && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                onClick={() => handleRecipientTypeChange("")}
                data-testid="reset-filter-btn"
              >
                {lang === "kn" ? "ಮರುಹೊಂದಿಸಿ" : "Reset"}
              </Button>
            )}
          </div>

          {/* Send Success Modal */}
          <Dialog open={!!sendSuccess} onOpenChange={(open) => { if (!open) setSendSuccess(""); }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-center gap-2 text-emerald-700">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                  {lang === "kn" ? "ಸಂದೇಶ ಕಳುಹಿಸಲಾಗಿದೆ" : "Message Sent"}
                </DialogTitle>
              </DialogHeader>
              <p className="text-center text-sm text-slate-600 py-2">{sendSuccess}</p>
              <DialogFooter className="justify-center sm:justify-center">
                <Button onClick={() => setSendSuccess("")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="success-dialog-close">
                  {lang === "kn" ? "ಮುಚ್ಚಿ" : "Close"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ===== APPLICANT FLOW ===== */}
          {recipientType === "applicant" && (
            <div className="space-y-3">
              {/* Cascading Location Filters */}
              <div className={`sm:w-1/2 relative flex items-end gap-2 flex-wrap border border-emerald-300 rounded-lg p-3 bg-emerald-50/30 transition-opacity ${loadingFilters ? "opacity-60 pointer-events-none" : ""}`}>
                {loadingFilters && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/50 rounded-lg">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                  </div>
                )}
                <div className="flex-1 min-w-[100px]">
                  <label className="text-[11px] font-bold text-slate-700 mb-0.5 block">{t("messaging.byHobli")}</label>
                  <Select value={selHobli || "all"} onValueChange={handleHobliChange} disabled={loadingFilters}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-hobli">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("messaging.allHoblis")}</SelectItem>
                      {filteredHoblis.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {isRevenue && (
                  <div className="flex-1 min-w-[100px]">
                    <label className="text-[11px] font-bold text-slate-700 mb-0.5 block">{t("messaging.byCircle")}</label>
                    <Select value={selCircle || "all"} onValueChange={handleCircleChange} disabled={loadingFilters}>
                      <SelectTrigger className="h-8 text-xs" data-testid="filter-circle">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("messaging.allCircles")}</SelectItem>
                        {memoizedCircles.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex-1 min-w-[100px]">
                  <label className="text-[11px] font-bold text-slate-700 mb-0.5 block">{t("messaging.byVillage")}</label>
                  <Select value={selVillage || "all"} onValueChange={handleVillageChange} disabled={loadingFilters}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-village">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("messaging.allVillages")}</SelectItem>
                      {memoizedVillages.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  size="sm"
                  onClick={handlePreviewApplicants}
                  disabled={previewing}
                  className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                  data-testid="preview-recipients-btn"
                >
                  {previewing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                  {t("messaging.preview")}
                </Button>
                {loadingFilters && <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />}
              </div>

              {/* Farmer List + Message side by side */}
              {showFarmers && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Card className="border-blue-300 sm:w-1/2">
                    <CardHeader className="py-2 px-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-xs flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-emerald-600" />
                          {t("messaging.previewResults")}
                        </CardTitle>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="text-[10px]">{farmers.length} {t("messaging.farmersFound")}</Badge>
                          <Badge variant="outline" className="text-[10px] text-emerald-700">{selectedFarmers.size} {t("messaging.selectedCount")}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="px-3 pb-3 pt-0">
                      {farmers.length > 0 ? (
                        <>
                          <div className="flex gap-2 mb-2">
                            <Button size="sm" variant="outline" className="text-sm font-semibold h-7 text-blue-600 border-blue-300 hover:bg-blue-50" onClick={() => setSelectedFarmers(new Set(farmers.map(f => f.farmer_id)))} data-testid="select-all-btn">
                              {t("messaging.selectAll")}
                            </Button>
                            <Button size="sm" variant="outline" className="text-sm font-semibold h-7 text-blue-600 border-blue-300 hover:bg-blue-50" onClick={() => setSelectedFarmers(new Set())} data-testid="deselect-all-btn">
                              {t("messaging.deselectAll")}
                            </Button>
                          </div>
                          <div className="max-h-[400px] overflow-y-auto border border-slate-200 rounded-lg divide-y">
                            {memoizedFarmers.map((f, idx) => (
                              <label key={f.farmer_id || `f-${idx}`} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-stone-50 cursor-pointer text-xs" data-testid={`farmer-row-${f.farmer_id || idx}`}>
                                <input
                                  type="checkbox"
                                  checked={selectedFarmers.has(f.farmer_id)}
                                  onChange={() => toggleFarmer(f.farmer_id)}
                                  className="rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate text-xs">{f.farmer_name}</p>
                                  <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
                                    <span><Phone className="w-2.5 h-2.5 inline mr-0.5" />{f.farmer_phone}</span>
                                    <span><MapPin className="w-2.5 h-2.5 inline mr-0.5" />{f.village}{f.circle ? `, ${f.circle}` : ""}</span>
                                    <span><FileText className="w-2.5 h-2.5 inline mr-0.5" />{f.application_count} {t("messaging.apps")}</span>
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-slate-500 text-center py-3">0 {t("messaging.farmersFound")}</p>
                      )}
                    </CardContent>
                  </Card>
                  {farmers.length > 0 && (
                    <div className="sm:w-1/2">
                      <MessageCompose
                      key={resetKey}
                      templates={templates}
                      lang={lang}
                      t={t}
                      selectedCount={selectedFarmers.size}
                      recipientTypeLabel={recipientTypeLabel}
                      onSend={handleSendMessage}
                    />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== OFFICIAL FLOW ===== */}
          {recipientType === "official" && (
            <div className="flex flex-col sm:flex-row gap-3">
              <Card className="sm:w-2/3 border-blue-300">
                <CardHeader className="py-2 px-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-emerald-600" />
                      {isRevenue ? (lang === "kn" ? "ಕಂದಾಯ ಅಧಿಕಾರಿಗಳು" : "Revenue Officials") : (lang === "kn" ? "ಸರ್ವೆ ಅಧಿಕಾರಿಗಳು" : "Survey Officials")}
                    </CardTitle>
                    <Badge variant="outline" className="text-xs text-emerald-700">{selectedOfficials.size} {t("messaging.selectedCount")}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  {loadingOfficials ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <OfficialGroup
                        title={lang === "kn" ? "ತಾಲೂಕು ಕಚೇರಿ ಅಧಿಕಾರಿಗಳು" : "Taluk Office Officials"}
                        officials={officeOfficials}
                        selected={selectedOfficials}
                        toggle={toggleOfficial}
                        selectAll={() => selectAllOfficials(officeOfficials)}
                        deselectAll={() => deselectAllOfficials(officeOfficials)}
                        desigLabel={desigLabel}
                        t={t}
                        testIdPrefix="office"
                      />
                      <OfficialGroup
                        title={lang === "kn" ? "ಕ್ಷೇತ್ರ ಕಚೇರಿ ಅಧಿಕಾರಿಗಳು" : "Field Office Officials"}
                        officials={fieldOfficials}
                        selected={selectedOfficials}
                        toggle={toggleOfficial}
                        selectAll={() => selectAllOfficials(fieldOfficials)}
                        deselectAll={() => deselectAllOfficials(fieldOfficials)}
                        desigLabel={desigLabel}
                        t={t}
                        testIdPrefix="field"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
              {(officeOfficials.length > 0 || fieldOfficials.length > 0) && (
                <div className="sm:w-1/3">
                  <MessageCompose
                    key={resetKey}
                    templates={templates}
                    lang={lang}
                    t={t}
                    selectedCount={selectedOfficials.size}
                    recipientTypeLabel={recipientTypeLabel}
                    onSend={handleSendMessage}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== HISTORY VIEW ===== */}
      {activeView === "history" && (
        <div className="sm:w-1/2 space-y-3">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">{t("messaging.noHistory")}</p>
            </div>
          ) : (
            <>
              {history.map((msg, idx) => {
                const isApplicant = msg.recipient_type === "applicant";
                const isEven = idx % 2 === 0;
                const borderColor = isEven ? "border-l-blue-500" : "border-l-amber-500";
                const headerBg = isEven ? "bg-blue-50" : "bg-amber-50";
                const headerText = isEven ? "text-blue-800" : "text-amber-800";
                const detailBg = isEven ? "bg-blue-50/40" : "bg-amber-50/40";
                const badgeClass = isApplicant ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700";
                return (
                <Card key={msg.id} className={`overflow-hidden border-l-4 ${borderColor}`} data-testid={`history-msg-${msg.id}`}>
                  <button
                    className={`w-full text-left px-4 py-3 flex items-center justify-between hover:brightness-95 ${headerBg}`}
                    onClick={() => setExpandedMsg(expandedMsg === msg.id ? null : msg.id)}
                    data-testid={`toggle-msg-${msg.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                          <CheckCircle className="w-2.5 h-2.5 mr-0.5" />
                          {t("messaging.delivered")}
                        </Badge>
                        <Badge className={`text-[10px] ${badgeClass}`}>
                          {isApplicant ? (lang === "kn" ? "ಅರ್ಜಿದಾರರು" : "Applicants") : (lang === "kn" ? "ಅಧಿಕಾರಿಗಳು" : "Officials")}
                        </Badge>
                        <span className="text-[10px] text-slate-500 font-medium">
                          {msg.sent_at ? format(new Date(msg.sent_at), "MMM dd, HH:mm") : ""}
                        </span>
                      </div>
                      <p className={`text-xs font-semibold truncate ${headerText}`}>{msg.message}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 font-medium">
                        {msg.recipient_count} {t("messaging.recipients")} | {msg.sent_by_name}
                      </p>
                    </div>
                    {expandedMsg === msg.id ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                  </button>
                  {expandedMsg === msg.id && (
                    <div className={`px-4 pb-3 border-t space-y-2 ${detailBg}`}>
                      <p className={`text-xs pt-2 whitespace-pre-wrap ${headerText}`}>{msg.message}</p>
                      {msg.filters_used && Object.keys(msg.filters_used).some(k => msg.filters_used[k]) && (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-[10px] text-slate-500 font-medium">{t("messaging.filtersUsed")}:</span>
                          {Object.entries(msg.filters_used).filter(([, v]) => v).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
                          ))}
                        </div>
                      )}
                      <div className="max-h-[150px] overflow-y-auto border rounded divide-y bg-white">
                        {(msg.recipients || []).map((r, ridx) => (
                          <div key={ridx} className="px-2 py-1.5 text-[10px] flex items-center justify-between gap-2">
                            <span className="font-medium truncate">{r.name}</span>
                            {r.designation && <Badge variant="outline" className="text-[9px] flex-shrink-0">{r.designation}</Badge>}
                            <span className="text-slate-400 flex-shrink-0">{r.phone}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
                );
              })}
              {historyTotal > 10 && (
                <div className="flex justify-center gap-2 pt-2">
                  <Button size="sm" variant="outline" disabled={historyPage <= 1} onClick={() => loadHistory(historyPage - 1)} className="text-xs h-8">Prev</Button>
                  <span className="text-xs text-slate-500 flex items-center">{historyPage} / {Math.ceil(historyTotal / 10)}</span>
                  <Button size="sm" variant="outline" disabled={historyPage >= Math.ceil(historyTotal / 10)} onClick={() => loadHistory(historyPage + 1)} className="text-xs h-8">Next</Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


/* ---------- Message Compose - isolated state, no parent re-renders ---------- */
const MessageCompose = React.memo(function MessageCompose({ templates, lang, t, selectedCount, recipientTypeLabel, onSend }) {
  const [message, setMessage] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [sending, setSending] = useState(false);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId && templateId !== "custom") {
      const tpl = templates.find(tp => tp.id === templateId);
      if (tpl) setMessage(lang === "kn" ? tpl.message_kn : tpl.message_en);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || selectedCount === 0) return;
    setSending(true);
    try {
      await onSend(message, selectedTemplate);
      setMessage("");
      setSelectedTemplate("");
    } catch (err) {
      // Skip toast if 403 demo block (global dialog handles it)
      if (err.response?.status !== 403) {
        const detail = err.response?.data?.detail;
        const errMsg = typeof detail === "string" ? detail : t("messaging.sendError");
        toast.error(errMsg);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-full">
      <div className="space-y-2 border-2 border-emerald-400 rounded-lg p-3">
        <Select value={selectedTemplate || ""} onValueChange={handleTemplateSelect}>
          <SelectTrigger className="h-8 text-xs max-w-[280px]" data-testid="template-select">
            <SelectValue placeholder={t("messaging.selectTemplate")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="custom">{t("messaging.noTemplate")}</SelectItem>
            {templates.map(tpl => (
              <SelectItem key={tpl.id} value={tpl.id}>
                {lang === "kn" ? tpl.title_kn : tpl.title_en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("messaging.messagePlaceholder")}
          rows={8}
          className="w-full text-sm border border-stone-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
          data-testid="message-textarea"
        />

        <Button
          size="sm"
          onClick={handleSend}
          disabled={sending || selectedCount === 0 || !message.trim()}
          className="w-full bg-emerald-600 hover:bg-emerald-700 h-8"
          data-testid="send-mms-btn"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
          {sending
            ? t("messaging.sending")
            : `${t("messaging.send")} → ${selectedCount} ${recipientTypeLabel}`}
        </Button>
      </div>
    </div>
  );
});


/* ---------- Official Group Sub-component ---------- */
function OfficialGroup({ title, officials, selected, toggle, selectAll, deselectAll, desigLabel, t, testIdPrefix }) {
  if (officials.length === 0) return null;
  const selectedInGroup = officials.filter(o => selected.has(o.id)).length;

  return (
    <div data-testid={`${testIdPrefix}-officials-group`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-slate-700">{title} ({officials.length})</h4>
        <div className="flex items-center gap-2">
          <button className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline" onClick={selectAll} data-testid={`${testIdPrefix}-select-all`}>
            {t("messaging.selectAll")}
          </button>
          <span className="text-xs text-slate-300">|</span>
          <button className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline" onClick={deselectAll} data-testid={`${testIdPrefix}-deselect-all`}>
            {t("messaging.deselectAll")}
          </button>
          <Badge variant="outline" className="text-[9px] ml-1">{selectedInGroup}/{officials.length}</Badge>
        </div>
      </div>
      <div className="max-h-[200px] overflow-y-auto border-2 border-amber-300 rounded-lg divide-y">
        {officials.map(o => (
          <label key={o.id} className="flex items-center gap-3 px-3 py-2 hover:bg-stone-50 cursor-pointer text-xs" data-testid={`official-row-${o.id}`}>
            <input
              type="checkbox"
              checked={selected.has(o.id)}
              onChange={() => toggle(o.id)}
              className="rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{o.name}</p>
              <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
                <Badge variant="outline" className="text-[9px]">{desigLabel(o.designation)}</Badge>
                <span><Phone className="w-2.5 h-2.5 inline mr-0.5" />{o.phone}</span>
                {o.assigned_hobli && <span><MapPin className="w-2.5 h-2.5 inline mr-0.5" />{o.assigned_hobli}</span>}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
